import React from "react";
import { Link } from "react-router-dom";
import { FaClock, FaCheckCircle } from "react-icons/fa";
import { GiSpeedBoat } from "react-icons/gi";
import { motion } from "framer-motion";

const FeaturedPackages = ({ packages }) => {
  return (
    <div className="bg-gradient-to-b from-white to-blue-50 dark:from-gray-900 dark:to-gray-800 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <span className="inline-block bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold px-5 py-2 rounded-full mb-4">
            Popular Choices
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-4">
            Featured Tour Packages
          </h2>
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Choose from our most popular tour packages and start your adventure
            today
          </p>
        </motion.div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((pkg, index) => {
            const containerVariants = {
              hidden: { opacity: 0, y: 50 },
              visible: {
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.5,
                  delay: index * 0.1,
                },
              },
            };

            return (
              <motion.div
                key={`${pkg.tourSlug}-${pkg.id}`}
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-cyan-400 dark:hover:border-cyan-500 hover:-translate-y-2 h-full flex flex-col">
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={pkg.image}
                      alt={pkg.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                    {/* Price Badge */}
                    <div className="absolute top-4 right-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-full shadow-xl">
                      <div className="text-lg font-bold">
                        €{pkg.price_per_person || pkg.price_per_group}
                      </div>
                      <div className="text-[10px] -mt-1">
                        {pkg.price_per_group
                          ? "per group"
                          : pkg.priceLabel || "per person"}
                      </div>
                    </div>

                    {/* Tour Type Badge */}
                    <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm text-gray-800 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                      <GiSpeedBoat className="text-cyan-600" />
                      {pkg.tourName}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                      {pkg.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 flex-1">
                      {pkg.description}
                    </p>

                    {/* Duration */}
                    <div className="flex items-center gap-2 mb-4 text-gray-700 dark:text-gray-300">
                      <FaClock className="text-cyan-600" />
                      <span className="text-sm font-medium">
                        {pkg.duration}
                      </span>
                    </div>

                    {/* Included Items */}
                    <div className="mb-4">
                      <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        What's Included:
                      </div>
                      <div className="space-y-1">
                        {pkg.included.slice(0, 2).map((item, idx) => (
                          <div
                            key={idx}
                            className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400"
                          >
                            <FaCheckCircle className="text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="line-clamp-1">{item}</span>
                          </div>
                        ))}
                        {pkg.included.length > 2 && (
                          <div className="text-xs text-cyan-600 dark:text-cyan-400 font-medium">
                            +{pkg.included.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Book Now Button */}
                    <Link
                      to={
                        pkg.isRequestOnly
                          ? pkg.linkTo
                          : `/${pkg.tourSlug}/tour-details`
                      }
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 shadow-md hover:shadow-xl text-center text-sm mt-auto"
                    >
                      {pkg.isRequestOnly
                        ? "Request This Tour →"
                        : "View Package →"}
                    </Link>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link
            to="/boat-tours"
            className="inline-flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-white font-bold py-4 px-8 rounded-full border-2 border-cyan-500 hover:bg-cyan-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
          >
            <GiSpeedBoat className="text-xl" />
            View All Tours
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default FeaturedPackages;
